import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useTypedSelector";
import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import { useTranslation } from "react-i18next";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import {
  Breadcrumb,
  BreadCrumbItem,
} from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { InvoiceTableFilters } from "../features/InvoiceTableFilters";
import {
  InvoiceFilters,
  InvoiceRow,
  InvoiceTable,
} from "../features/InvoiceTable";
import { useState } from "react";
import {
  exportInvoiceData,
  getInvoiceData,
} from "../../infrastructure/redux/InvoiceDataSlice";
import { InfoCircleFill } from "react-bootstrap-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export const InvoicesPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("common");
  const title = t("Invoices");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const { filters } = useAppSelector(
    (state: {
      invoiceData: {
        loading: boolean;
        data: InvoiceRow[];
        filters: InvoiceFilters;
      };
    }) => state.invoiceData,
  );

  const buildQueryArgs = () => {
    const args: Partial<InvoiceFilters> & {
      inOutStorageFrom?: string;
      inOutStorageUntil?: string;
    } = {};
    if (filters?.relationId) args.relationId = filters.relationId;
    if (filters?.status) args.status = filters.status;
    if (filters?.InStorageFrom) args.inOutStorageFrom = filters.InStorageFrom;
    if (filters?.InStorageUntil)
      args.inOutStorageUntil = filters.InStorageUntil;
    return args;
  };

  const handleExport = async (invoice: boolean) => {
    setIsExporting(true);

    try {
      const args = {
        ...buildQueryArgs(),
        invoice,
        status: filters.status || "on_invoice",
      };
      const result = await dispatch(exportInvoiceData(args)).unwrap();

      setIsExporting(false);
      if (result && typeof result === "object") {
        const url = (result as { url?: string }).url;
        if (url) {
          window.open(url, "_blank");
        }
      }
      if (invoice) {
        void dispatch(getInvoiceData(buildQueryArgs()));
      }
    } catch (err) {
      setIsExporting(false);
      console.error("Error exporting invoice data:", err);
    }
  };

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title} />
          <Breadcrumb>
            <BreadCrumbItem>{title}</BreadCrumbItem>
          </Breadcrumb>
          <LayoutHeaderActions>
            {isExporting ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : null}
            {filters.status != "to_invoice" ? (
              <OverlayTrigger
                overlay={
                  <Tooltip className="custom-tooltip">
                    {t("The export is only available for 'to invoice'")}
                  </Tooltip>
                }
              >
                <InfoCircleFill size={16} color="#b11e17" className={"me-1"} />
              </OverlayTrigger>
            ) : null}

            <button
              type="button"
              className="btn btn-outline-primary btn-sm me-2"
              disabled={isExporting || filters.status !== "to_invoice"}
              onClick={() => void handleExport(false)}
            >
              {t("Download export")}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={isExporting || filters.status !== "to_invoice"}
              onClick={() => void handleExport(true)}
            >
              {t("Export and invoiced")}
            </button>
          </LayoutHeaderActions>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader title={title} className="mb-3" />
          <InvoiceTableFilters />
          <InvoiceTable />
        </Content>
      </LayoutContent>
    </Layout>
  );
};
